import { create } from "zustand";

import { useMutateUser, useUser } from "../user";
import { AnalyticsEvent } from "./analytics-event";
import { AnalyticsEventType } from "./analytics-event-type";
import { useGoogleAnalyticsEvent } from "./use-google-analytics-event";

export function useAnalyticsEvent(): useAnalyticsEventReturn {
  const { submitEvent: submitGoogleEvent } = useGoogleAnalyticsEvent();
  const { reportBoardEvent } = useTwentyBoardEvents();

  const submitEvent = (event: AnalyticsEvent): void => {
    try {
      submitGoogleEvent(event);
    } catch (error) {
      // TODO: Direct report to sentry
    }
    reportBoardEvent?.(event);
  };

  return { submitEvent };
}

export type useAnalyticsEventReturn = {
  submitEvent: (event: AnalyticsEvent) => void;
};

function useTwentyBoardEvents(): useTwentyBoardEventsReturn {
  const { user } = useUser();
  const { updateUser } = useMutateUser();
  const { submitEvent: submitGoogleEvent } = useGoogleAnalyticsEvent();

  // eslint-disable-next-line max-statements
  const reportBoardEvent = (event: AnalyticsEvent): void => {
    if (!boardEventTypes.includes(event.type)) return;
    if (user.converted) return;

    const { count, increment, reset } = useStore.getState();
    const next = count + 1;
    if (next < 20) {
      increment();
      return;
    }

    try {
      submitGoogleEvent({
        type: AnalyticsEventType.TwentyBoardEvents,
      });
    } catch (error) {
      // TODO: Direct report to sentry
    }
    updateUser({ ...user, converted: true });
    reset();
  };

  return {
    reportBoardEvent,
  };
}

type useTwentyBoardEventsReturn = {
  reportBoardEvent?: (event: AnalyticsEvent) => void;
};

const boardEventTypes: AnalyticsEventType[] = [
  AnalyticsEventType.AddToken,
  AnalyticsEventType.MoveToken,
  AnalyticsEventType.RemoveToken,
  AnalyticsEventType.DrawDrawing,
  AnalyticsEventType.EraseDrawing,
  AnalyticsEventType.DrawLine,
  AnalyticsEventType.EraseLine,
  AnalyticsEventType.WriteText,
  AnalyticsEventType.EditText,
  AnalyticsEventType.ChangeTextColor,
  AnalyticsEventType.ChangeTextSize,
  AnalyticsEventType.MoveText,
  AnalyticsEventType.RemoveText,
];

const useStore = create<StoreState>((set) => ({
  count: 0,
  increment: () => set((state) => ({ count: state.count + 1 })),
  reset: () => set(() => ({ count: 0 })),
}));

type StoreState = {
  count: number;
  increment: () => void;
  reset: () => void;
};
